import React, { useEffect, useState } from "react";
import {
  X,
  Copy,
  CheckCircle,
  Clock,
  XCircle,
  Ban,
  UserIcon,
  Calendar,
  DollarSign,
  TrendingUp,
  Eye,
} from "lucide-react";
import { User } from "../../../../types/features/administration/User";
import { usersService } from "../../../../services/administration/usersService";
import { Sale, SaleStatus } from "../../../../types/features/administration/BuyAndSell";
import { UserDetails } from "../users/UserDetailsModal";

interface SaleDetailsModalProps {
  sale: Sale | null;
  isOpen: boolean;
  onClose: () => void;
  onSaleUpdated?: () => void;
}

const SaleDetailsModal: React.FC<SaleDetailsModalProps> = ({ sale, isOpen, onClose, onSaleUpdated }) => {
  const [vendeurData, setVendeurData] = useState<User | null>(null);
  const [acheteurData, setAcheteurData] = useState<User | null>(null);
  const [loadingVendeur, setLoadingVendeur] = useState(false);
  const [loadingAcheteur, setLoadingAcheteur] = useState(false);

  // États pour les modals utilisateur
  const [showVendeurDetails, setShowVendeurDetails] = useState(false);
  const [showAcheteurDetails, setShowAcheteurDetails] = useState(false);

  // Fonction pour récupérer les informations utilisateur complètes
  const fetchUserData = async (userId: string): Promise<User | null> => {
    if (!userId) return null;

    try {
      const token = localStorage.getItem("token");
      if (!token) return null;

      const response = await usersService.getUserWithKYC(userId, token);

      // Transformer les données au format User attendu par UserDetails
      if (response && response.data?.user) {
        const userData = response.data?.user;

        return {
          id: userData.id,
          name: `${userData.last_name || ""}   ${userData.first_name || ""}`,
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email || "",
          country: userData.country || "Non renseigné",
          countryFlag: userData.countryFlag || "🏳️",
          kycStatus: userData.kyc_status || userData.kycStatus || "not_requested",
          riskLevel: userData.riskLevel || "low",
          interact_email: userData.interact_email || "",
          registrationDate: userData.created_at || userData.registrationDate || new Date().toISOString(),
          documents: userData.documents || { id: false, passport: false, address: false, selfie: false },
          lastActivity: userData.lastActivity || userData.updated_at || new Date().toISOString(),
          status: userData.status || "INACTIVE",
          phonenumber: userData.phonenumber,
          is_verified: userData.is_verified,
          connexion_type: userData.connexion_type,
          role: userData.role,
          country_id: userData.country_id,
          created_at: userData.created_at,
          updated_at: userData.updated_at,
          password: userData.password,
          is_deleted: userData.is_deleted,
          fcm_token: userData.fcm_token,
          kyc_status: userData.kyc_status,
          has_kyc: userData.has_kyc,
          birthdate: userData.birthdate,
          expired_document_date: userData.expired_document_date,
        };
      }

      return null;
    } catch (error) {
      console.error(`Erreur lors de la récupération des infos utilisateur ${userId}:`, error);
      return null;
    }
  };

  useEffect(() => {
    if (sale && isOpen) {
      // Récupérer les infos du vendeur
      if (sale.id_user_vendeur) {
        setLoadingVendeur(true);
        fetchUserData(sale.id_user_vendeur).then((data) => {
          setVendeurData(data);
          setLoadingVendeur(false);
        });
      }

      // Récupérer les infos de l'acheteur
      if (sale.id_user_acheteur) {
        setLoadingAcheteur(true);
        fetchUserData(sale.id_user_acheteur).then((data) => {
          setAcheteurData(data);
          setLoadingAcheteur(false);
        });
      }
    }
  }, [sale, isOpen]);

  if (!isOpen || !sale) return null;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert("Copié dans le presse-papier");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusConfig = (status: SaleStatus) => {
    switch (status) {
      case SaleStatus.COMPLETED:
        return {
          bg: "bg-emerald-50",
          text: "text-emerald-700",
          border: "border-emerald-200",
          icon: CheckCircle,
          label: "Complété",
        };
      case SaleStatus.PENDING:
        return { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", icon: Clock, label: "En Attente" };
      case SaleStatus.CANCELLED:
        return { bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200", icon: Ban, label: "Annulé" };
      default:
        return { bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200", icon: Clock, label: status };
    }
  };

  const StatusIcon = getStatusConfig(sale.status).icon;

  // Composant pour afficher un résumé utilisateur avec bouton voir détails
  const UserSummaryCard: React.FC<{
    userData: User | null;
    loading: boolean;
    title: string;
    userId: string;
    onViewDetails: () => void;
  }> = ({ userData, loading, title, userId, onViewDetails }) => {
    if (loading) {
      return (
        <div className="p-4 space-y-3 bg-gray-50 rounded-xl animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-200 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="w-32 h-4 bg-gray-200 rounded" />
              <div className="w-24 h-3 bg-gray-200 rounded" />
            </div>
          </div>
        </div>
      );
    }

    if (!userData) {
      return (
        <div className="p-4 bg-gray-50 rounded-xl text-center">
          <p className="text-sm text-gray-500">Utilisateur non trouvé</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl">
          <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full">
            <span className="text-base font-bold text-white">
              {userData.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .substring(0, 2)}
            </span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="text-lg font-bold text-gray-800">{userData?.name} </h4>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  userData.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                }`}
              >
                {userData.status === "ACTIVE" ? "Actif" : "Inactif"}
              </span>
            </div>
            <p className="text-sm text-gray-500 font-mono">{userData?.email}</p>
          </div>
          <button
            onClick={onViewDetails}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
          >
            <Eye className="w-4 h-4" />
            Voir les détails
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">Email</p>
            <p className="text-sm font-medium text-gray-800 truncate">{userData.email || "Non renseigné"}</p>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">Téléphone</p>
            <p className="text-sm font-medium text-gray-800">{userData.phonenumber || "Non renseigné"}</p>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">Pays</p>
            <p className="text-sm font-medium text-gray-800">
              {userData.countryFlag} {userData.country}
            </p>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">Statut KYC</p>
            <span
              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                userData.kycStatus === "approved"
                  ? "bg-green-100 text-green-700"
                  : userData.kycStatus === "pending"
                    ? "bg-yellow-100 text-yellow-700"
                    : "bg-gray-100 text-gray-700"
              }`}
            >
              {userData.kycStatus === "approved"
                ? "Approuvé"
                : userData.kycStatus === "pending"
                  ? "En attente"
                  : "Non soumis"}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex overflow-y-auto fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
        <div className="my-8 w-full max-w-5xl bg-white rounded-2xl shadow-2xl">
          {/* Header */}
          <div className="flex flex-shrink-0 justify-between items-center px-6 py-5 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-2xl">
            <div>
              <h2 className="text-xl font-bold text-white">Détails de la Vente</h2>
              <p className="mt-1 text-sm text-blue-100 font-mono">ID: {sale.id}</p>
            </div>
            <button onClick={onClose} className="p-1 text-white rounded-lg transition-colors hover:bg-blue-800">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
            <div className="p-6 space-y-6">
              {/* Status Banner */}
              <div
                className={`p-4 rounded-xl border-2 ${getStatusConfig(sale.status).bg} ${getStatusConfig(sale.status).border}`}
              >
                <div className="flex items-center gap-3">
                  <StatusIcon className={`w-8 h-8 ${getStatusConfig(sale.status).text}`} />
                  <div>
                    <p className="text-sm font-semibold text-gray-600">Statut actuel</p>
                    <p className={`text-xl font-bold ${getStatusConfig(sale.status).text}`}>
                      {getStatusConfig(sale.status).label}
                    </p>
                  </div>
                </div>
              </div>

              {/* Two columns layout - User Info */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Seller Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 border-b-2 border-orange-200 pb-2">
                    <UserIcon className="w-5 h-5 text-orange-600" />
                    Informations Vendeur
                    <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs">Seller</span>
                  </h3>

                  <UserSummaryCard
                    userData={vendeurData}
                    loading={loadingVendeur}
                    title="Vendeur"
                    userId={sale.id_user_vendeur}
                    onViewDetails={() => setShowVendeurDetails(true)}
                  />

                  {/* Transaction Info Seller */}
                  <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-xs font-semibold text-orange-600 mb-1">Transaction Vendeur</p>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm">{sale.id_transaction_vendeur || "N/A"}</span>
                      {sale.id_transaction_vendeur && (
                        <button
                          onClick={() => copyToClipboard(sale.id_transaction_vendeur!)}
                          className="p-1 text-orange-400 hover:text-orange-600"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column - Buyer Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 border-b-2 border-green-200 pb-2">
                    <UserIcon className="w-5 h-5 text-green-600" />
                    Informations Acheteur
                    <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">Buyer</span>
                  </h3>

                  {sale.id_user_acheteur ? (
                    <>
                      <UserSummaryCard
                        userData={acheteurData}
                        loading={loadingAcheteur}
                        title="Acheteur"
                        userId={sale.id_user_acheteur}
                        onViewDetails={() => setShowAcheteurDetails(true)}
                      />

                      {/* Transaction Info Buyer */}
                      <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-xs font-semibold text-green-600 mb-1">Transaction Acheteur</p>
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-sm">{sale.id_transaction_acheteur || "N/A"}</span>
                          {sale.id_transaction_acheteur && (
                            <button
                              onClick={() => copyToClipboard(sale.id_transaction_acheteur!)}
                              className="p-1 text-green-400 hover:text-green-600"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-xl text-center">
                      <p className="text-gray-500">Acheteur non assigné</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Financial Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 border-b-2 border-blue-200 pb-2">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                  Détails Financiers
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border-2 border-orange-200">
                    <p className="text-xs font-semibold text-orange-600 uppercase">Vente</p>
                    <p className="text-2xl font-black text-orange-700">
                      {sale.somme_a_vendre} {sale.monaie_vente}
                    </p>
                    <p className="text-sm text-orange-600">Taux: {sale.taux_vendeur}</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border-2 border-emerald-200">
                    <p className="text-xs font-semibold text-emerald-600 uppercase">Achat</p>
                    <p className="text-2xl font-black text-emerald-700">
                      {sale.somme_a_recevoir} {sale.monaie_achat}
                    </p>
                    <p className="text-sm text-emerald-600">Taux: {sale.taux_marchand}</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border-2 border-purple-200">
                    <p className="text-xs font-semibold text-purple-600 uppercase">Marge Marchand</p>
                    <p className="text-2xl font-black text-purple-700">
                      {sale.somme_marchand} {sale.monaie_achat}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <TrendingUp className="w-3 h-3 text-purple-600" />
                      <p className="text-xs text-purple-600">
                        {((sale.somme_marchand / sale.somme_a_recevoir) * 100).toFixed(2)}% de marge
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 border-b-2 border-gray-200 pb-2">
                  <Calendar className="w-5 h-5 text-gray-600" />
                  Informations Complémentaires
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-semibold text-gray-500">Pays Vendeur ID</p>
                    <p className="text-lg font-bold text-gray-800">{sale.id_pays_vendeur}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-semibold text-gray-500">Créé le</p>
                    <p className="text-lg font-bold text-gray-800">{formatDate(sale.created_at)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-semibold text-gray-500">Dernière mise à jour</p>
                    <p className="text-lg font-bold text-gray-800">{formatDate(sale.updated_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-shrink-0 gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl border-t-2 border-gray-200">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 font-bold text-gray-700 rounded-lg border-2 border-gray-300 hover:bg-gray-100"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>

      {/* UserDetails Modal for Seller */}
      {showVendeurDetails && vendeurData && (
        <UserDetails
          user={vendeurData}
          onClose={() => setShowVendeurDetails(false)}
          onApprove={() => {
            // Rafraîchir les données du vendeur après approbation KYC
            if (sale?.id_user_vendeur) {
              fetchUserData(sale.id_user_vendeur).then(setVendeurData);
            }
            setShowVendeurDetails(false);
          }}
          onReject={() => {
            // Rafraîchir les données du vendeur après rejet KYC
            if (sale?.id_user_vendeur) {
              fetchUserData(sale.id_user_vendeur).then(setVendeurData);
            }
            setShowVendeurDetails(false);
          }}
          onUserUpdate={() => {
            // Rafraîchir les données du vendeur après mise à jour
            if (sale?.id_user_vendeur) {
              fetchUserData(sale.id_user_vendeur).then(setVendeurData);
            }
          }}
        />
      )}

      {/* UserDetails Modal for Buyer */}
      {showAcheteurDetails && acheteurData && (
        <UserDetails
          user={acheteurData}
          onClose={() => setShowAcheteurDetails(false)}
          onApprove={() => {
            // Rafraîchir les données de l'acheteur après approbation KYC
            if (sale?.id_user_acheteur) {
              fetchUserData(sale.id_user_acheteur).then(setAcheteurData);
            }
            setShowAcheteurDetails(false);
          }}
          onReject={() => {
            // Rafraîchir les données de l'acheteur après rejet KYC
            if (sale?.id_user_acheteur) {
              fetchUserData(sale.id_user_acheteur).then(setAcheteurData);
            }
            setShowAcheteurDetails(false);
          }}
          onUserUpdate={() => {
            // Rafraîchir les données de l'acheteur après mise à jour
            if (sale?.id_user_acheteur) {
              fetchUserData(sale.id_user_acheteur).then(setAcheteurData);
            }
          }}
        />
      )}
    </>
  );
};

export default SaleDetailsModal;
